import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, FC, MutableRefObject, PropsWithChildren, SetStateAction } from 'react';
import { useLocation } from 'react-router-dom';
import { ShippingOptionType } from '../../bff-api/baskets/model';
import { useProduct } from '../../bff-api/products/useProduct';
import { useStoreBopisInventory } from '../../bff-api/stores/useStoreBopisInventory';
import { useStoreSDDInventory } from '../../bff-api/stores/useStoreSDDInventory';
import { useGTM } from '../../hooks';
import { GTMPageType } from '../../hooks/gtm/model/GTMPageType';
import { useBopisStore } from '../../hooks/useBopisStore';
import { useRefTracker } from '../../hooks/useRefTracker';
import { useSDDAddress } from '../../hooks/useSDDAddress';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { noop } from '../../utils/utils';
import { DEFAULT_INVENTORY, getInventories, getPDPInventoryFlags } from '../utils/inventory-utils';
import type { BFFHttpError } from '../../bff-api/model/BFFHttpError';
import type { Product } from '../../bff-api/products/model';
import type {
    GetElementRef,
    RegisterElementExistingRef,
    RegisterElementRef,
} from '../../hooks/useRefTracker';
import type { RemoveFieldOfType } from '../../types/helpers';
import type { Inventories, Inventory, InventoryFlags } from '../utils/types';

const { SHIP_TO, BOPIS, SDD } = ShippingOptionType;

interface InventoryStore {
    currentInventory: Inventory;
    inventories: Inventories;
    flags: Partial<InventoryFlags>;
}

interface IPdpContext {
    isError: boolean;
    error?: BFFHttpError | null;
    selectedQuantity: number;
    setSelectedQuantity: Dispatch<number>;
    product?: Product;
    selectedShippingOptionType: ShippingOptionType;
    setSelectedShippingOptionType: Dispatch<ShippingOptionType>;
    inventoryStore: InventoryStore;
    isLoading: boolean;
    isLoadingInventory: boolean;
    isFetchingCurrentInventory: boolean;
    isLoadingCurrentInventory: boolean;
    isLoadingProductInventory: boolean;
    isLoadingBopisInventory: boolean;
    isLoadingSDDInventory: boolean;
    shouldShowStickyBuyBoxAddToBagButton: boolean;
    setShouldShowStickyBuyBoxAddToBagButton: Dispatch<SetStateAction<boolean>>;
    availableQuantityToAdd: number | null;
    setAvailableQuantityToAdd: Dispatch<SetStateAction<number | null>>;
    registerElementRef: RegisterElementRef;
    getElementRef: GetElementRef;
    registerElementExistingRef: RegisterElementExistingRef;
    giftCardAmount?: string | null;
    setGiftCardAmount: Dispatch<SetStateAction<string | null>>;
}

const PdpContext = createContext<IPdpContext>({
    isError: false,
    selectedQuantity: 1,
    setSelectedQuantity: noop,
    selectedShippingOptionType: SHIP_TO,
    setSelectedShippingOptionType: noop,
    inventoryStore: {
        inventories: {
            [SHIP_TO]: DEFAULT_INVENTORY,
            [BOPIS]: DEFAULT_INVENTORY,
            [SDD]: DEFAULT_INVENTORY,
        },
        currentInventory: DEFAULT_INVENTORY,
        flags: {},
    },
    isLoading: false,
    isLoadingInventory: false,
    isFetchingCurrentInventory: false,
    isLoadingCurrentInventory: false,
    isLoadingProductInventory: false,
    isLoadingBopisInventory: false,
    isLoadingSDDInventory: false,
    shouldShowStickyBuyBoxAddToBagButton: false,
    setShouldShowStickyBuyBoxAddToBagButton: noop,
    availableQuantityToAdd: null,
    setAvailableQuantityToAdd: noop,
    registerElementRef: () => () => ({ current: null }),
    getElementRef: () => ({ current: null }),
    registerElementExistingRef: noop,
    giftCardAmount: null,
    setGiftCardAmount: noop,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PdpContextInitialValues = Partial<RemoveFieldOfType<IPdpContext, Dispatch<any>>>;

export type PdpProviderProps = PropsWithChildren<{
    initialValues?: PdpContextInitialValues;
    productId?: string;
}>;

export const PdpProvider: FC<PdpProviderProps> = ({ children, initialValues, productId }) => {
    const { data: product, isLoading, isFetching, isError, error } = useProduct(productId);
    const [selectedQuantity, setSelectedQuantity] = useState(initialValues?.selectedQuantity ?? 1);
    const [selectedShippingOptionType, setSelectedShippingOptionType] =
        useState<ShippingOptionType>(
            initialValues?.selectedShippingOptionType ??
                product?.c_shippingOptions.find(({ enabled }) => enabled)?.type ??
                SHIP_TO,
        );

    const elementRefs = useRef(new Map<string, MutableRefObject<HTMLElement | null>>());
    const { registerElementRef, getElementRef, registerElementExistingRef } =
        useRefTracker(elementRefs);
    const [giftCardAmount, setGiftCardAmount] = useState<string | null>(null);

    const [shouldShowStickyBuyBoxAddToBagButton, setShouldShowStickyBuyBoxAddToBagButton] =
        useState(false);
    const [availableQuantityToAdd, setAvailableQuantityToAdd] = useState<number | null>(null);

    const { data: sddAddress } = useSDDAddress();
    const { data: store } = useBopisStore();

    const { onPdpPageView, updatePageData } = useGTM();
    const location = useLocation();

    useEffect(() => {
        if (productId && product) {
            const isVariant = product.variants?.some((variant) => variant.productId === productId);

            if (!isVariant) {
                setSelectedQuantity(1);
                setSelectedShippingOptionType(ShippingOptionType.SHIP_TO);
            }

            setAvailableQuantityToAdd(null);
        }
    }, [location]);

    useEffect(() => {
        if (
            product &&
            product.id !== window.pageData?.product?.id &&
            window.pageData?.pageType === GTMPageType.PDP
        ) {
            onPdpPageView(product);
        }
    }, [product]);

    useEffect(
        () => () => {
            updatePageData({
                product: undefined,
            });
        },
        [],
    );

    const isProductEnabled = !!product?.id;
    const isLoadingProductInventory = isLoading && isProductEnabled;

    const isBopisInventoryEnabled = isProductEnabled && !!store?.id;
    const bopisInventory = useStoreBopisInventory(
        store?.id,
        product?.id,
        selectedQuantity,
        isBopisInventoryEnabled,
    );
    const isLoadingBopisInventory = bopisInventory.isLoading && isBopisInventoryEnabled;

    const isSDDInventoryEnabled = !!(
        isProductEnabled &&
        sddAddress?.c_latitude &&
        sddAddress?.c_longitude
    );
    const sddInventory = useStoreSDDInventory(
        product?.id,
        selectedQuantity,
        sddAddress?.c_latitude,
        sddAddress?.c_longitude,
        isSDDInventoryEnabled,
    );
    const isLoadingSDDInventory = sddInventory.isLoading && isSDDInventoryEnabled;

    const inventories = useMemo(
        () => getInventories(product?.inventory, bopisInventory.data, sddInventory.data),
        [product, bopisInventory, sddInventory],
    );

    const currentInventory = inventories[selectedShippingOptionType];

    const thresholdOnlyXLeft = useCustomPreferenceOrDefault<number>(
        CustomPreference.THRESHOLD_ONLY_X_LEFT_BADGE,
        5,
    );
    const thresholdLimitedQuantity = useCustomPreferenceOrDefault<number>(
        CustomPreference.THRESHOLD_LIMITED_QUANTITY_BADGE,
        10,
    );

    const onChangeShippingOptionType = useMemo(
        () => (newShippingOptionType: ShippingOptionType) => {
            setSelectedShippingOptionType(newShippingOptionType);
            setAvailableQuantityToAdd(null);
        },
        [],
    );

    const flags: Partial<InventoryFlags> = useMemo(
        () =>
            product?.id
                ? getPDPInventoryFlags({
                      productId: product?.id,
                      quantity: selectedQuantity,
                      currentInventory,
                      shipToInventory: inventories[ShippingOptionType.SHIP_TO],
                      thresholdOnlyXLeft,
                      thresholdLimitedQuantity,
                  })
                : {},
        [
            product?.id,
            selectedQuantity,
            currentInventory,
            inventories,
            thresholdOnlyXLeft,
            thresholdLimitedQuantity,
        ],
    );

    const ctx: IPdpContext = useMemo(
        () => ({
            isError,
            error,
            selectedQuantity,
            setSelectedQuantity,
            product,
            selectedShippingOptionType,
            setSelectedShippingOptionType: onChangeShippingOptionType,
            inventoryStore: {
                inventories,
                currentInventory,
                flags,
            },
            isLoadingInventory:
                isLoadingProductInventory || isLoadingBopisInventory || isLoadingSDDInventory,
            isLoadingProductInventory,
            isLoadingBopisInventory,
            isLoadingSDDInventory,
            isLoadingCurrentInventory:
                (selectedShippingOptionType === SHIP_TO && isLoadingProductInventory) ||
                (selectedShippingOptionType === BOPIS && isLoadingBopisInventory) ||
                (selectedShippingOptionType === SDD && isLoadingSDDInventory),
            isFetchingCurrentInventory:
                (selectedShippingOptionType === SHIP_TO && isFetching) ||
                (selectedShippingOptionType === BOPIS && bopisInventory.isFetching) ||
                (selectedShippingOptionType === SDD && sddInventory.isFetching),
            isLoading,
            shouldShowStickyBuyBoxAddToBagButton,
            setShouldShowStickyBuyBoxAddToBagButton,
            availableQuantityToAdd,
            setAvailableQuantityToAdd,
            registerElementRef,
            getElementRef,
            registerElementExistingRef,
            giftCardAmount,
            setGiftCardAmount,
        }),
        [
            selectedQuantity,
            setSelectedQuantity,
            product,
            selectedShippingOptionType,
            onChangeShippingOptionType,
            inventories,
            currentInventory,
            flags,
            isFetching,
            bopisInventory.isFetching,
            sddInventory.isFetching,
            isLoading,
            isLoadingProductInventory,
            isLoadingBopisInventory,
            isLoadingSDDInventory,
            error,
            isError,
            shouldShowStickyBuyBoxAddToBagButton,
            setShouldShowStickyBuyBoxAddToBagButton,
            availableQuantityToAdd,
            setAvailableQuantityToAdd,
            registerElementRef,
            getElementRef,
            registerElementExistingRef,
            giftCardAmount,
        ],
    );

    return <PdpContext.Provider value={ctx}>{children}</PdpContext.Provider>;
};

PdpProvider.defaultProps = {
    initialValues: undefined,
    productId: undefined,
};

export const usePDP = (): IPdpContext => {
    const ctx = useContext(PdpContext);
    if (!ctx) {
        throw new Error('usePDP must be used within PdpContextProvider');
    }
    return ctx;
};
